import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import RemoteGame, { OnlineSession } from "../play/game/RemoteGame";
import PlayNowGame from "./PlayNowGame";
import musicController from "../../audio/music-controller";
import { subscribeLobbyCounts, LobbyCounts } from "../../online/net";
import { useAuth } from "../../shared/auth";
import { isAdmin } from "../../shared/admin";
import { flagUrl, flagUrlSquare, isFlagCode } from "../../shared/flags";
import { useLandscapePhone } from "../../shared/useResponsive";
import { getLocalPlayer } from "../../shared/local-players";
import { wormColors } from "core/src/core/constants.js";
import { RoyaleInfo, RoyClock, RoySeats } from "../lobby/start/parts";
import st from "../lobby/start/start.module.scss";
import styles from "./modes.module.scss";

// /royale: joining the next Royale, waiting for it (or playing Play Now
// meanwhile, and being pulled in when it starts), and the Royale itself.
const ROYALE_SIZE = 30;

const mmss = (t: number) => `${Math.floor(t / 60)}:${String(t % 60).padStart(2, "0")}`;
const hexPts = (cx: number, cy: number, r: number) =>
  Array.from({ length: 6 }, (_, k) => {
    const a = (Math.PI / 180) * (60 * k - 90);
    return `${(cx + r * Math.cos(a)).toFixed(1)},${(cy + r * Math.sin(a)).toFixed(1)}`;
  }).join(" ");
const Crown = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 8l4 3.5L12 5l4 6.5L20 8l-1.6 10H5.6L4 8z" fill="currentColor" fillOpacity="0.25" /><path d="M6 21h12" /></svg>);

export default function RoyalePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [joinKey, setJoinKey] = useState(0);
  // ?m= names the Royale's room (a reload lands back in it); ?now=1 opens straight
  // into "play while you wait".
  const roomId = useMemo(() => {
    if (joinKey > 0) return "public:royale";
    const m = (searchParams.get("m") || "").trim();
    return /^[A-Za-z0-9_-]{1,32}$/.test(m) ? "public:royale#" + m : "public:royale";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joinKey]);
  const onRoomInstance = useCallback((instance: string) => {
    const hash = instance.indexOf("#");
    if (hash <= 0) return;
    const next = new URLSearchParams(window.location.search);
    next.set("m", instance.slice(hash + 1));
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [session, setSession] = useState<OnlineSession | null>(null);
  const [counts, setCounts] = useState<LobbyCounts>({});
  useEffect(() => subscribeLobbyCounts(setCounts), []);
  const [playNow, setPlayNow] = useState(() => searchParams.get("now") === "1");
  const waiting = !session || session.phase === "connecting" || session.phase === "waiting";
  // The Royale started (or we are back in one): Play Now gives way.
  const started = !!session && !waiting;
  useEffect(() => { if (started) setPlayNow(false); }, [started]);
  // The match-found sound, once as it starts (a ref, so a re-run can't repeat it).
  const wasStarted = useRef(false);
  useEffect(() => {
    if (started && !wasStarted.current) musicController.play("match-found");
    wasStarted.current = started;
  }, [started]);

  const [, tick] = useState(0);
  useEffect(() => {
    if (!waiting) return;
    const t = setInterval(() => tick((n) => n + 1), 500);
    return () => clearInterval(t);
  }, [waiting]);

  const leave = useCallback(() => navigate("/"), [navigate]);
  // Left the Royale's queue from the Play Now game you were passing the time in:
  // the Royale lets go of your seat, the game you are in carries on.
  const [leftQueue, setLeftQueue] = useState(false);
  // The next Royale: drop the finished one's id and sign up afresh.
  const again = useCallback(() => {
    const next = new URLSearchParams(window.location.search);
    next.delete("m");
    setSearchParams(next, { replace: true });
    setSession(null);
    setJoinKey((k) => k + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const againAndPlay = useCallback(() => { again(); setPlayNow(true); }, [again]);

  const box: any = counts.royale || {};
  const number = box.number || 0;
  const lastWin = box.lastWinner as { name?: string; flag?: string | null } | undefined;
  const roster = session?.players ?? [];
  const signed = Math.max(roster.length, 0);
  const secs = session?.startAt ? Math.max(0, Math.ceil((session.startAt - Date.now()) / 1000)) : 0;
  const pct = Math.max(0, Math.min(100, (signed / ROYALE_SIZE) * 100));
  const joinedFlags = Array.from(new Set(roster.map((p: any) => p.flag).filter((f: any): f is string => !!f && isFlagCode(f)))).slice(0, 8);
  const land = useLandscapePhone();
  // Which waiting player is me: the room's word once it has assigned my seat,
  // until then the one going by my name.
  const assigned: string[] = (session as any)?.youIds ?? [];
  const myName = useMemo(() => getLocalPlayer(0, "").name, []);
  const byName = (session?.players ?? []).find((pl: any) => pl.type === "human" && pl.name === myName);
  const mineIds: string[] = assigned.length ? assigned : byName ? [byName.id] : [];
  const connecting = !session || session.phase === "connecting" || roster.length === 0;
  const starting = !connecting && secs <= 0;
  const title = "Royale";

  return (
    <div className={styles.page}>
      {!leftQueue && (
        <div className={`${styles.game} page-main page-main-game ${playNow && waiting ? styles.hidden : ""}`}>
          <RemoteGame key={joinKey} roomId={roomId} onSession={setSession} onRoomInstance={onRoomInstance} onLeave={leave} onPlayAgain={again} onPlayNow={againAndPlay} />
        </div>
      )}

      {waiting && !playNow && (
        <div className={styles.cover}>
          {connecting ? (
            <div className={styles.findCard}>
              <div className={`${styles.radar} ${styles.radarGold}`}><i /><i /><i /></div>
              <h3>Joining</h3>
              <button className={styles.ghost} onClick={leave}>Cancel</button>
            </div>
          ) : !land ? (
            // PC: a gold band (the Royale, its clock), then a seat for each of the
            // thirty, the players who are in on theirs (the cards the opening shows)
            <div className={styles.lobby}>
              <button className={styles.waitX} onClick={leave} aria-label="Leave">×</button>
              <div className={styles.lobbyBand}><Crown /><b>ROYALE</b><span className={styles.lobbyClock}><RoyaleInfo /><RoyClock secs={secs} /></span></div>
              {/* a box for each of the thirty, the taken ones gold (the bar of the start page's Royale card) */}
              <div className={styles.lobbyBar}><RoySeats pct={pct} /></div>
              <div className={styles.lobbyHead}>
                <span><b>{signed}</b>/ {ROYALE_SIZE}</span>
                {lastWin && lastWin.name ? <span className={styles.lobbyLast}>Last won by {lastWin.flag ? <img src={flagUrlSquare(lastWin.flag)} alt="" /> : null}<b>{lastWin.name}</b></span> : null}
              </div>
              <div className={styles.lobbySeats}>
                {Array.from({ length: ROYALE_SIZE }, (_, i) => {
                  const pl: any = roster[i];
                  return pl
                    ? <span key={pl.id} className={`${styles.lobbySeat} ${mineIds.indexOf(pl.id) !== -1 ? styles.lobbyYou : ""}`} style={{ "--c": (wormColors as Record<string, string>)[pl.colorId] || "#28c4d4" } as React.CSSProperties}><img src={flagUrlSquare(pl.flag || "xx")} alt="" /><b>{pl.name}</b></span>
                    : <span key={"e" + i} className={`${styles.lobbySeat} ${styles.lobbyEmpty}`} />;
                })}
              </div>
              {starting
                ? <div className={styles.pullRow}><span className={styles.spin} />Starting now</div>
                : <button className={`${styles.waitPlay} ${styles.lobbyPlay}`} onClick={() => setPlayNow(true)}>Play while you wait</button>}
              {isAdmin(user) && !starting && <button className={styles.admin} onClick={() => session?.startNow()}>Start now</button>}
            </div>
          ) : (
            <div className={styles.wait}>
              <button className={styles.waitX} onClick={leave} aria-label="Leave">×</button>
              {/* the start page's Royale card: the crown and who won last, the clock, the thirty seats */}
              <div className={`${st.roy} ${st.royE} ${st.royStill} ${styles.waitCard}`}>
                <span className={st.royBlock}>
                  <Crown />
                  {lastWin && lastWin.name ? <span className={st.royPlate}>{lastWin.flag ? <img src={flagUrl(lastWin.flag)} alt="" /> : null}<b>{lastWin.name}</b></span> : null}
                </span>
                <span className={st.royBody}>
                  <span className={st.royRow}>
                    <span className={st.royName}>ROYALE</span>
                    <span className={st.royClockRow}><RoyaleInfo /><RoyClock secs={secs} /></span>
                  </span>
                  <span className={st.royTag}>Up to <b>30 players</b><i />only one survives</span>
                  <RoySeats pct={pct} />
                </span>
              </div>
              <div className={styles.waitRow}>
                {/* who is in so far: their flags, and how many beside you */}
                <span className={styles.waitWho}>
                  {starting ? <span className={styles.spin} /> : joinedFlags.length > 0 ? <span className={styles.waitFlags}>{joinedFlags.map((f) => <img key={f} src={flagUrlSquare(f)} alt="" />)}</span> : null}
                  <span>{starting ? "Starting now" : signed <= 1 ? "You are first in" : <>You and <b>{signed - 1}</b> {signed - 1 === 1 ? "other" : "others"}</>}</span>
                </span>
                {!starting && <button className={styles.waitPlay} onClick={() => setPlayNow(true)}>Play while you wait</button>}
              </div>
              {/* Starting it before the clock is an admin-only dev tool. */}
              {isAdmin(user) && !starting && <button className={styles.admin} onClick={() => session?.startNow()}>Start now</button>}
            </div>
          )}
        </div>
      )}

      {waiting && playNow && (
        <PlayNowGame onLeave={leftQueue ? leave : () => setPlayNow(false)}>
          {/* the Royale you are queued for, in the game's top line: the clock,
              how full it is, and the way back to the queue */}
          {!leftQueue && <div className={styles.queue}>
            <span className={styles.queueGold}><Crown /></span>
            <span className={styles.queueText}><small>Royale in</small><b className={styles.fixed}>{mmss(secs).split("").map((ch, k) => <i key={k} className={ch === ":" ? styles.colon : undefined}>{ch}</i>)}</b></span>
            <span className={styles.queueSeats}><b>{signed}</b>/{ROYALE_SIZE}</span>
            <button className={styles.queueBack} onClick={() => setPlayNow(false)}>Back to queue</button>
            <button className={styles.queueX} onClick={() => { setLeftQueue(true); setSession(null); }} aria-label="Leave the Royale queue">×</button>
          </div>}
        </PlayNowGame>
      )}
    </div>
  );
}
